/**
 * Quick Delivery Estimate — planning-only composition of shared market pricing
 * plus a separate internal Mayker cost estimate for comparison.
 * Does not save records or create projects.
 */

import {
  MARKETS,
  determineMarketByDistance,
  evaluateStandardDeliveryFee,
  evaluateExtendedOvernightFee,
  EXTENDED_OVERNIGHT_RATE_PER_MEMBER,
} from './markets';
import { QUICK_ESTIMATE_ASSUMPTIONS } from './quickEstimateAssumptions';

export const LONG_HAUL_NEXT_STEP =
  'Contact JP before quoting long-distance or specialty deliveries.';

/**
 * Display label for market classification in the estimate drawer.
 */
export function getEstimateMarketLabel(market) {
  if (!market) return '';
  if (market.id === 'local') return 'Nashville Market';
  if (market.id === 'stretch') return 'Long-Haul Market';
  return `${market.name} Market`;
}

/**
 * Legacy fallback only — do not use for complimentary eligibility when event days
 * are known. Admin Proposal quotes use load-in → strike (inclusive) event days.
 * Prefer an explicit `eventDays` input (default 1 for single-day planning).
 */
export function inferEventDaysFromNightsAway(nightsAway) {
  const nights = Math.max(0, parseInt(nightsAway, 10) || 0);
  return Math.max(1, nights + 1);
}

/**
 * Market-included overnight nights (customer pricing allowance). Useful as a
 * planning hint for Nights Away — Admin may still adjust from the travel plan.
 */
export function suggestedNightsForMarket(market) {
  if (!market || market.standardNights == null) return null;
  return Math.max(0, market.standardNights);
}

function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Estimate proposal add-ons from a product/rental total so QDE can derive the
 * invoice basis used for delivery fee % — matching Admin defaults:
 *   product care = 10% of product
 *   service fee  = 5% of (product + product care + delivery)
 *
 * For planning, `deliveryFee` defaults to 0 (fee not yet applied).
 * Entered product is treated as the proposal product/rental basis (no discount).
 */
export function estimateProposalFeeAddOns(
  productTotal,
  {
    deliveryFee = 0,
    productCareRate = QUICK_ESTIMATE_ASSUMPTIONS.productCareRate,
    serviceFeeRate = QUICK_ESTIMATE_ASSUMPTIONS.serviceFeeRate,
  } = {}
) {
  const product = Math.max(0, parseFloat(productTotal) || 0);
  const delivery = Math.max(0, parseFloat(deliveryFee) || 0);
  const productCare = roundMoney(product * productCareRate);
  const serviceFee = roundMoney((product + productCare + delivery) * serviceFeeRate);
  const invoicePreTaxExDelivery = roundMoney(product + productCare + serviceFee);

  return {
    product,
    productCare,
    serviceFee,
    deliveryFee: delivery,
    invoicePreTaxExDelivery,
    productCareRate,
    serviceFeeRate,
  };
}

/**
 * Resolve invoice + product from QDE inputs.
 *
 * Default (Admin-aligned planning):
 *   product from productTotal / rentalValue
 *   invoice = product + estimated product care + estimated service fee
 *
 * Explicit `invoiceTotal` still overrides when provided (tests / advanced use).
 */
export function resolveEstimateMoneyInputs({
  invoiceTotal,
  productTotal,
  rentalValue,
} = {}) {
  const hasProduct = productTotal != null && productTotal !== '';
  const hasInvoice = invoiceTotal != null && invoiceTotal !== '';
  const hasRental = rentalValue != null && rentalValue !== '';

  const product = Math.max(
    0,
    parseFloat(hasProduct ? productTotal : hasRental ? rentalValue : 0) || 0
  );

  const addOns = estimateProposalFeeAddOns(product);

  if (hasInvoice) {
    return {
      invoice: Math.max(0, parseFloat(invoiceTotal) || 0),
      product,
      invoiceEstimated: false,
      ...addOns,
    };
  }

  return {
    invoice: addOns.invoicePreTaxExDelivery,
    product,
    invoiceEstimated: true,
    ...addOns,
  };
}

export function roomsRequiredForCrew(crewMembers, membersPerRoom = QUICK_ESTIMATE_ASSUMPTIONS.teamMembersPerRoom) {
  const crew = Math.max(0, parseInt(crewMembers, 10) || 0);
  if (crew <= 0) return 0;
  return Math.ceil(crew / membersPerRoom);
}

function parseMoney(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n) || n <= 0) return 0;
  return n;
}

/**
 * Internal planning cost — independent of customer delivery fee rules.
 * Lodging uses all nights away (pricing "included travel" does not reduce cost).
 */
export function buildMaykerCostEstimate({
  crewMembers = 0,
  crewDays = 1,
  nightsAway = 0,
  distanceMiles = null,
  trucks = QUICK_ESTIMATE_ASSUMPTIONS.defaultTruckCount,
  truckRental = 0,
  flights = 0,
  otherDirectCosts = 0,
  assumptions = QUICK_ESTIMATE_ASSUMPTIONS,
} = {}) {
  const crew = Math.max(0, parseInt(crewMembers, 10) || 0);
  const days = Math.max(1, parseInt(crewDays, 10) || 1);
  const nights = Math.max(0, parseInt(nightsAway, 10) || 0);
  const truckCount = Math.max(0, parseInt(trucks, 10) || 0);
  const rental = parseMoney(truckRental);
  const airfare = parseMoney(flights);
  const other = parseMoney(otherDirectCosts);
  const rooms = roomsRequiredForCrew(crew, assumptions.teamMembersPerRoom);

  const labor = crew > 0 ? crew * days * assumptions.crewCostPerDay : 0;
  const lodging =
    nights > 0 && rooms > 0
      ? rooms * nights * assumptions.hotelRoomPerNight
      : 0;

  const oneWay =
    distanceMiles == null || Number.isNaN(Number(distanceMiles))
      ? null
      : Math.max(0, Number(distanceMiles));
  const roundTripMiles = oneWay != null ? oneWay * 2 : null;

  let fuel = 0;
  let fuelAvailable = false;
  if (roundTripMiles != null && roundTripMiles > 0 && truckCount > 0 && assumptions.truck26Mpg > 0) {
    const gallons = (roundTripMiles / assumptions.truck26Mpg) * truckCount;
    fuel = Math.round(gallons * assumptions.planningGasPricePerGallon);
    fuelAvailable = true;
  }

  const estimatedDirectCost = labor + lodging + fuel + rental + airfare + other;
  const contingencyRate = assumptions.contingencyRate ?? 0;
  const contingency = Math.round(estimatedDirectCost * contingencyRate);
  const total = estimatedDirectCost + contingency;

  return {
    assumptions: {
      crewCostPerDay: assumptions.crewCostPerDay,
      hotelRoomPerNight: assumptions.hotelRoomPerNight,
      teamMembersPerRoom: assumptions.teamMembersPerRoom,
      truck26Mpg: assumptions.truck26Mpg,
      planningGasPricePerGallon: assumptions.planningGasPricePerGallon,
      contingencyRate,
    },
    crewMembers: crew,
    crewDays: days,
    nightsAway: nights,
    rooms,
    trucks: truckCount,
    oneWayMiles: oneWay,
    roundTripMiles,
    labor,
    lodging,
    fuel,
    fuelAvailable,
    truckRental: rental,
    flights: airfare,
    otherDirectCosts: other,
    estimatedDirectCost,
    contingency,
    contingencyRate,
    total,
  };
}

/**
 * Compare customer delivery estimate vs internal planning cost.
 */
export function buildEstimateVsCost(customerDeliveryTotal, maykerCost) {
  const customer =
    typeof customerDeliveryTotal === 'number' && !Number.isNaN(customerDeliveryTotal)
      ? customerDeliveryTotal
      : null;
  const cost = maykerCost?.total;
  if (customer == null || cost == null) {
    return {
      comparable: false,
      customerDeliveryEstimate: customer,
      estimatedMaykerCost: cost ?? null,
      estimatedContribution: null,
    };
  }
  return {
    comparable: true,
    customerDeliveryEstimate: customer,
    estimatedMaykerCost: cost,
    estimatedContribution: customer - cost,
  };
}

/**
 * Build a planning estimate aligned with Admin Proposal fee assembly:
 * - product drives complimentary threshold
 * - invoice drives % / minimum fee
 * - by default invoice is estimated from product + 10% product care + 5% service fee
 * - eventDays (load-in → strike inclusive) drives multi-day complimentary rules
 *
 * `rentalValue` remains as a backward-compatible alias for productTotal.
 * Explicit `invoiceTotal` still overrides the estimated invoice when provided.
 */
export function buildQuickDeliveryEstimate({
  invoiceTotal,
  productTotal,
  rentalValue,
  market,
  distanceMiles = null,
  nightsAway = 0,
  crewMembers = 0,
  crewDays = 1,
  trucks = QUICK_ESTIMATE_ASSUMPTIONS.defaultTruckCount,
  truckRental = 0,
  flights = 0,
  otherDirectCosts = 0,
  /**
   * Inclusive event days (load-in → strike). Defaults to 1 for single-day
   * planning — does NOT infer from nightsAway (that mismatched Admin quotes).
   */
  eventDays = 1,
}) {
  const money = resolveEstimateMoneyInputs({
    invoiceTotal,
    productTotal,
    rentalValue,
  });
  const { invoice, product, invoiceEstimated, productCare, serviceFee, productCareRate, serviceFeeRate } = money;
  const nights = Math.max(0, parseInt(nightsAway, 10) || 0);
  const crew = Math.max(0, parseInt(crewMembers, 10) || 0);
  const days = Math.max(1, parseInt(eventDays, 10) || 1);

  if (!market) {
    return { ok: false, error: 'Select or resolve a delivery market.' };
  }

  const feeEval = evaluateStandardDeliveryFee({
    invoice,
    product,
    market,
    eventDays: days,
  });

  const overnightRequired = nights > 0;
  const overnight = evaluateExtendedOvernightFee({
    market,
    nights,
    teamMembers: crew,
    overnightRequired,
  });

  const includedNights =
    market.standardNights == null ? null : Math.max(0, market.standardNights);
  const additionalNights = overnight.additionalNights || 0;
  const suggestedNights = suggestedNightsForMarket(market);

  const maykerCost = buildMaykerCostEstimate({
    crewMembers: crew,
    crewDays,
    nightsAway: nights,
    distanceMiles,
    trucks,
    truckRental,
    flights,
    otherDirectCosts,
  });

  const isLongHaul = market.feePercentage == null || market.id === 'stretch';
  // Rental minimum is a product/rental guideline (same intent as Admin proposal).
  const belowRentalMinimum =
    !isLongHaul && market.rentalMinimum != null && product > 0 && product < market.rentalMinimum;

  const shared = {
    market,
    marketLabel: getEstimateMarketLabel(market),
    distanceMiles,
    invoiceTotal: invoice,
    productTotal: product,
    /** @deprecated alias of productTotal — kept for older result consumers */
    rentalValue: product,
    invoiceEstimated: !!invoiceEstimated,
    estimatedProductCare: productCare,
    estimatedServiceFee: serviceFee,
    productCareRate,
    serviceFeeRate,
    eventDays: days,
    nightsAway: nights,
    crewMembers: crew,
    includedNights,
    additionalNights,
    suggestedNights,
    belowRentalMinimum,
    maykerCost,
  };

  if (isLongHaul) {
    return {
      ok: true,
      kind: 'longHaul',
      ...shared,
      nextStep: LONG_HAUL_NEXT_STEP,
      detail: market.customPricingDetail,
      comparison: buildEstimateVsCost(null, maykerCost),
    };
  }

  const complimentary = feeEval.standardFee === 'waived';
  const standardNumeric = typeof feeEval.standardFee === 'number' ? feeEval.standardFee : 0;
  const totalFee = complimentary
    ? overnight.extendedFee
    : standardNumeric + overnight.extendedFee;

  return {
    ok: true,
    kind: complimentary ? 'complimentary' : 'standard',
    ...shared,
    overnightRate: EXTENDED_OVERNIGHT_RATE_PER_MEMBER,
    complimentary,
    complimentaryBlockedByMultiDay: feeEval.complimentaryBlockedByMultiDay,
    freeThreshold: market.freeThreshold,
    feePercentage: market.feePercentage,
    minFee: market.minFee,
    feeBasis: feeEval.feeBasis,
    feeFromPercentage: feeEval.feeFromPercentage,
    standardFee: feeEval.standardFee,
    baseDeliveryAmount: complimentary ? 0 : standardNumeric,
    extendedFee: overnight.extendedFee,
    totalFee,
    amountToThreshold: feeEval.amountToThreshold,
    comparison: buildEstimateVsCost(totalFee, maykerCost),
  };
}

export function resolveMarketFromDistance(distanceMiles) {
  return determineMarketByDistance(distanceMiles);
}

export { MARKETS, determineMarketByDistance, QUICK_ESTIMATE_ASSUMPTIONS };
